import React, { useRef, useState } from 'react'
import classNames from 'classnames'
import { Button, Input, Spinner } from 'components/ui'
import { HiPaperAirplane, HiPaperClip, HiX } from 'react-icons/hi'
import { apiUploadChatAttachment } from 'services/ChatService'

const MAX_CHAT_ATTACHMENTS = 3
const ALLOWED_CHAT_ATTACHMENT_MIME_TYPES = new Set([
    'image/png',
    'image/jpeg',
    'image/webp',
    'image/gif',
])

const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error(`Unable to read ${file?.name || 'image'} from disk.`))
        reader.readAsDataURL(file)
    })

const getImageDimensions = (dataUrl) =>
    new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => {
            resolve({ width: image.width || null, height: image.height || null })
        }
        image.onerror = () => reject(new Error('Unable to inspect image dimensions.'))
        image.src = dataUrl
    })

const extractUploadErrorMessage = (error) => {
    const apiMessage = error?.response?.data?.message || error?.response?.data?.error
    return String(apiMessage || error?.message || 'Unable to upload chat image.').trim()
}

const ChatInput = ({
    onSendMessage,
    disabled = false,
    loading = false,
    isDark = false,
    variant = 'default',
    placeholder = '',
}) => {
    const [message, setMessage] = useState('')
    const [attachments, setAttachments] = useState([])
    const [uploadingCount, setUploadingCount] = useState(0)
    const [attachmentError, setAttachmentError] = useState('')
    const fileInputRef = useRef(null)
    const isUploading = uploadingCount > 0
    const readyAttachments = attachments.filter(
        (attachment) => attachment?.status !== 'uploading'
    )
    const canSubmit =
        !disabled && !isUploading && (message.trim().length > 0 || readyAttachments.length > 0)

    const removeAttachment = (attachmentId) => {
        setAttachments((currentAttachments) =>
            currentAttachments.filter((attachment) => attachment.id !== attachmentId)
        )
    }

    const triggerFilePicker = () => {
        if (!disabled) {
            fileInputRef.current?.click()
        }
    }

    const uploadImageFile = async (file) => {
        if (!file) {
            return null
        }

        const mimeType = String(file.type || '').trim().toLowerCase()
        if (!ALLOWED_CHAT_ATTACHMENT_MIME_TYPES.has(mimeType)) {
            throw new Error('Only PNG, JPEG, WEBP, and GIF images can be attached right now.')
        }

        const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
        const dataUrl = await readFileAsDataUrl(file)
        const { width, height } = await getImageDimensions(dataUrl)

        setUploadingCount((count) => count + 1)
        setAttachments((currentAttachments) => [
            ...currentAttachments,
            {
                id: pendingId,
                type: 'image',
                fileName: file.name || 'Image',
                mimeType,
                sizeBytes: Number(file.size) || null,
                width,
                height,
                previewUrl: dataUrl,
                status: 'uploading',
            },
        ])

        try {
            const response = await apiUploadChatAttachment({
                fileName: file.name || 'Image',
                mimeType,
                sizeBytes: Number(file.size) || 0,
                width,
                height,
                dataUrl,
            })
            const uploadedAttachment =
                response?.data?.data?.attachment || response?.data?.attachment || null

            if (!uploadedAttachment?.id) {
                throw new Error('Upload completed without returning attachment metadata.')
            }

            setAttachments((currentAttachments) =>
                currentAttachments.map((attachment) =>
                    attachment.id === pendingId
                        ? {
                            ...attachment,
                            ...uploadedAttachment,
                            status: 'ready',
                        }
                        : attachment
                )
            )

            return uploadedAttachment
        } catch (error) {
            setAttachments((currentAttachments) =>
                currentAttachments.filter((attachment) => attachment.id !== pendingId)
            )
            throw error
        } finally {
            setUploadingCount((count) => Math.max(0, count - 1))
        }
    }

    const addFiles = async (fileList) => {
        const selectedFiles = Array.from(fileList || []).filter(Boolean)
        if (selectedFiles.length === 0) {
            return
        }

        setAttachmentError('')
        const remainingCapacity = Math.max(0, MAX_CHAT_ATTACHMENTS - attachments.length)
        if (remainingCapacity <= 0) {
            setAttachmentError(`You can attach up to ${MAX_CHAT_ATTACHMENTS} images per message.`)
            return
        }

        const filesToUpload = selectedFiles.slice(0, remainingCapacity)
        if (selectedFiles.length > remainingCapacity) {
            setAttachmentError(`Only the first ${remainingCapacity} image${remainingCapacity === 1 ? '' : 's'} were added.`)
        }

        for (const file of filesToUpload) {
            try {
                await uploadImageFile(file)
            } catch (error) {
                setAttachmentError(extractUploadErrorMessage(error))
            }
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        if (!canSubmit) {
            return
        }

        const result = onSendMessage({
            text: message.trim(),
            attachments: readyAttachments,
        })
        setMessage('')
        setAttachments([])
        setAttachmentError('')
        await Promise.resolve(result)
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSubmit(e)
        }
    }

    const handleFileInputChange = async (e) => {
        await addFiles(e.target.files)
        if (e.target) {
            e.target.value = ''
        }
    }

    const handlePaste = async (e) => {
        const imageFiles = Array.from(e.clipboardData?.items || [])
            .map((item) => (item.kind === 'file' ? item.getAsFile() : null))
            .filter(
                (file) =>
                    file &&
                    ALLOWED_CHAT_ATTACHMENT_MIME_TYPES.has(
                        String(file.type || '').trim().toLowerCase()
                    )
            )

        if (imageFiles.length > 0) {
            e.preventDefault()
            await addFiles(imageFiles)
        }
    }

    const resolvedPlaceholder =
        placeholder ||
        (isUploading
            ? 'Preparing your image…'
            : loading
                ? 'Waiting for response…'
                : variant === 'workspace'
                    ? 'Ask about tickets, notes, KB docs, or the client environment… Paste or attach an image when helpful. Use Shift + Enter for newline.'
                    : 'Type your message or paste an image…')

    const attachmentStrip = attachments.length > 0 ? (
        <div className="mb-3 flex flex-wrap gap-3">
            {attachments.map((attachment) => (
                <div key={attachment.id} className="w-[92px]">
                    <div
                        className={classNames(
                            'relative h-[92px] overflow-hidden rounded-xl border shadow-sm',
                            isDark
                                ? 'border-gray-700 bg-gray-800'
                                : 'border-gray-200 bg-gray-50'
                        )}
                    >
                        {attachment.previewUrl ? (
                            <img
                                src={attachment.previewUrl}
                                alt={attachment.fileName || 'Attached image'}
                                className="h-full w-full object-cover"
                            />
                        ) : null}
                        <button
                            type="button"
                            onClick={() => removeAttachment(attachment.id)}
                            className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white transition hover:bg-black/80"
                            aria-label={`Remove ${attachment.fileName || 'image'}`}
                        >
                            <HiX className="h-3.5 w-3.5" />
                        </button>
                        {attachment.status === 'uploading' ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                                <Spinner size={18} />
                            </div>
                        ) : null}
                    </div>
                    <p
                        className={classNames(
                            'mt-1 truncate text-[11px] leading-4',
                            isDark ? 'text-gray-300' : 'text-gray-600'
                        )}
                        title={attachment.fileName || 'Image'}
                    >
                        {attachment.fileName || 'Image'}
                    </p>
                </div>
            ))}
        </div>
    ) : null

    const attachmentStatusText = attachmentError
        ? attachmentError
        : isUploading
            ? 'Uploading image…'
            : attachments.length > 0
                ? `${readyAttachments.length} image${readyAttachments.length === 1 ? '' : 's'} ready`
                : ''

    if (variant === 'workspace') {
        return (
            <form onSubmit={handleSubmit} className="p-4">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={handleFileInputChange}
                />
                <div
                    className={classNames(
                        'relative rounded-2xl border px-4 py-3 shadow-sm',
                        isDark
                            ? 'border-gray-700 bg-gray-900/70'
                            : 'border-gray-200 bg-white'
                    )}
                >
                    {attachmentStrip}
                    <textarea
                        value={message}
                        rows={3}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onPaste={handlePaste}
                        placeholder={resolvedPlaceholder}
                        disabled={disabled}
                        className={classNames(
                            'block max-h-40 min-h-[88px] w-full resize-none border-0 bg-transparent p-0 pb-14 pr-28 text-sm leading-6 outline-none placeholder:text-gray-400 focus:outline-none focus:ring-0',
                            isDark
                                ? 'text-gray-100 disabled:text-gray-500'
                                : 'text-gray-900 disabled:text-gray-400'
                        )}
                    />

                    <div className="absolute bottom-3 left-3 flex items-center gap-3">
                        <button
                            type="button"
                            onClick={triggerFilePicker}
                            disabled={disabled || attachments.length >= MAX_CHAT_ATTACHMENTS}
                            className={classNames(
                                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition',
                                isDark
                                    ? 'bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:bg-gray-800/60 disabled:text-gray-500'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:bg-gray-100 disabled:text-gray-400'
                            )}
                        >
                            <HiPaperClip className="h-4 w-4" />
                            Attach image
                        </button>

                        {attachmentStatusText ? (
                            <span
                                className={classNames(
                                    'text-xs',
                                    attachmentError
                                        ? 'text-red-500'
                                        : isDark
                                            ? 'text-gray-400'
                                            : 'text-gray-500'
                                )}
                            >
                                {attachmentStatusText}
                            </span>
                        ) : null}
                    </div>

                    <Button
                        type="submit"
                        disabled={!canSubmit}
                        size="sm"
                        variant="solid"
                        icon={loading ? <Spinner size={16} /> : <HiPaperAirplane />}
                        className={classNames(
                            'absolute bottom-3 right-3 min-w-[108px] justify-center px-4 shadow-sm transition-opacity',
                            !canSubmit && 'opacity-50'
                        )}
                    >
                        {loading ? 'Thinking…' : 'Send'}
                    </Button>
                </div>
            </form>
        )
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-3 p-3">
            <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={handleFileInputChange}
            />

            {attachmentStrip}

            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <Input
                        value={message}
                        textArea={true}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onPaste={handlePaste}
                        placeholder={resolvedPlaceholder}
                        disabled={disabled}
                        className="flex-1"
                        size="sm"
                    />
                    {attachmentStatusText ? (
                        <p
                            className={classNames(
                                'mt-2 text-xs',
                                attachmentError
                                    ? 'text-red-500'
                                    : isDark
                                        ? 'text-gray-400'
                                        : 'text-gray-500'
                            )}
                        >
                            {attachmentStatusText}
                        </p>
                    ) : null}
                </div>

                <div className="flex flex-col gap-2">
                    <button
                        type="button"
                        onClick={triggerFilePicker}
                        disabled={disabled || attachments.length >= MAX_CHAT_ATTACHMENTS}
                        className={classNames(
                            'inline-flex h-9 w-9 items-center justify-center rounded-full border transition',
                            isDark
                                ? 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:bg-gray-800/60 disabled:text-gray-500'
                                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-100 disabled:bg-gray-50 disabled:text-gray-300'
                        )}
                        aria-label="Attach image"
                    >
                        <HiPaperClip className="h-4 w-4" />
                    </button>

                    <Button
                        type="submit"
                        disabled={!canSubmit}
                        size="sm"
                        icon={loading ? <Spinner size={16} /> : <HiPaperAirplane />}
                        className={classNames(
                            'px-3 flex-shrink-0 transition-opacity',
                            !canSubmit && 'opacity-50'
                        )}
                    />
                </div>
            </div>
        </form>
    )
}

export default ChatInput